import React from 'react'
import { fetchEvents } from '@/api/eventApi'
import { EVENT_QUERY_KEYS } from '@/api/queryKeys'
import { InfiniteSelect, type SelectOption } from '../InfiniteSelect'

export interface EventSelectValue {
  id: string
  name: string
}

interface EventSelectProps {
  value?: EventSelectValue | EventSelectValue[] | null
  onValueChange: (event: EventSelectValue | EventSelectValue[] | null) => void
  placeholder?: string
  label?: string
  required?: boolean
  className?: string
  error?: string
  disabled?: boolean
  statusFilter?: string[]
  categoryFilter?: string[]
  multiple?: boolean
  maxSelections?: number
  variant?: 'default' | 'compact'
  enableSearch?: boolean
}

export function EventSelect({
  value,
  onValueChange,
  placeholder = 'Select event...',
  label = 'Event',
  required = false,
  className = '',
  error,
  disabled = false,
  statusFilter,
  categoryFilter,
  multiple = false,
  maxSelections,
  variant = 'default',
  enableSearch = true,
}: Readonly<EventSelectProps>) {
  const transformEvent = (event: EventSelectValue): SelectOption => ({
    id: event.id,
    label: event.name,
    value: event.id,
    eventData: event,
  })

  const selectedOptions = React.useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value.map(transformEvent) : []
    } else {
      return value && !Array.isArray(value) ? transformEvent(value) : null
    }
  }, [value, multiple])

  const handleValueChange = (options: SelectOption | SelectOption[] | null) => {
    if (!options) {
      onValueChange(null)
      return
    }

    if (multiple && Array.isArray(options)) {
      const events: EventSelectValue[] = options
        .map(option => option.eventData as EventSelectValue)
        .filter(Boolean)

      onValueChange(events.length > 0 ? events : null)
    } else if (!multiple && !Array.isArray(options)) {
      const option = options

      if (option.eventData && typeof option.eventData === 'object') {
        onValueChange(option.eventData as EventSelectValue)
      } else {
        // Fallback reconstruction from option data
        const reconstructedEvent: EventSelectValue = {
          id: option.id,
          name: option.label,
        }
        onValueChange(reconstructedEvent)
      }
    }
  }

  const additionalParams = React.useMemo(() => {
    const params: Record<string, unknown> = {
      fetchNamesOnly: true,
    }

    if (statusFilter && statusFilter.length > 0) {
      params.status = statusFilter
    }

    if (categoryFilter && categoryFilter.length > 0) {
      params.categories = categoryFilter
    }

    return params
  }, [statusFilter, categoryFilter])

  const resolvedPlaceholder =
    multiple && placeholder === 'Select event...'
      ? 'Select events...'
      : placeholder

  const compactClassName =
    variant === 'compact'
      ? 'w-56 h-9 border-ss-neutral-200 bg-transparent hover:border-ss-blue-700 focus:border-ss-blue-700'
      : className

  return (
    <InfiniteSelect<EventSelectValue>
      queryKey={[...EVENT_QUERY_KEYS.all, 'names-only']}
      queryFn={fetchEvents}
      transformData={transformEvent}
      value={selectedOptions}
      onValueChange={handleValueChange}
      placeholder={resolvedPlaceholder}
      label={variant === 'compact' ? undefined : label}
      required={required}
      className={compactClassName}
      error={error}
      disabled={disabled}
      searchPlaceholder="Search events..."
      emptyMessage="No events available"
      noResultsMessage="No events found"
      pageSize={20}
      additionalParams={additionalParams}
      enableSearch={enableSearch}
      showAvatar={false}
      multiple={multiple}
      maxSelections={maxSelections}
    />
  )
}
